import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { usePartnersReadAccountHistory } from '@/api/hooks';
import { type Partner } from '@/api/routes/partners/partners.api';
import Chip from '@/components/Chip';
import { IconShieldCheck } from '@/components/icon/tabler/IconShieldCheck';
import AccountListSkeleton from '@/domain/account/components/AccountListSkeleton';
import { type AccountHistoryTone } from '@/domain/account/types';
import { formatAccountDate, formatHistoryDateTime, getHistoryTone } from '@/domain/account/utils';
import { partnerStatusLabel } from '@/domain/account-status/utils';
import { useTranslate } from '@/state/i18n';
import { controlTokens, radiusTokens } from '@/theme/themePrimitives';

interface Props {
  partner: Partner;
}

/** P15 · the account status and its timeline. Bodyprint decisions read "Equipe Bodyprint". */
const AccountStatusCard = ({ partner }: Props) => {
  const t = useTranslate('domain.account.components.AccountStatusCard');
  const { data: items, isLoading, isError, refetch } = usePartnersReadAccountHistory();
  const approved = partner.status === 'approved';

  return (
    <Paper variant="outlined" sx={sx.paper}>
      <Stack direction="row" alignItems="center" gap={1}>
        <IconShieldCheck sx={{ fontSize: 20, color: 'text.secondary' }} />
        <Typography variant="subtitle2" fontWeight={700}>
          {t('title')}
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
        <Chip
          size="xsmall"
          label={partnerStatusLabel[partner.status]}
          color={approved ? 'success' : 'neutral'}
          variant={approved ? 'solid' : 'outline'}
        />
        {partner.reviewedAt ? (
          <Typography variant="caption" color="text.secondary">
            {t('since', { date: formatAccountDate(partner.reviewedAt) })}
          </Typography>
        ) : null}
      </Stack>
      {isLoading ? <AccountListSkeleton rows={2} /> : null}
      {isError ? (
        <Typography variant="body2" color="text.secondary">
          {t('loadError')}{' '}
          <Link component="button" type="button" onClick={() => refetch()}>
            {t('retry')}
          </Link>
        </Typography>
      ) : null}
      {items ? (
        <Box component="ol" sx={sx.list}>
          {items.map((item, index) => (
            <Box component="li" key={item.id} sx={sx.item}>
              <Box sx={sx.gutter}>
                <Box sx={sx.dot(getHistoryTone(item.type))} />
                {index < items.length - 1 ? <Box sx={sx.line} /> : null}
              </Box>
              <Stack gap={0.25} sx={sx.content}>
                <Typography variant="body2" fontWeight={600}>
                  {t(`entries.${item.type}`)}
                </Typography>
                {item.note ? (
                  <Typography variant="body2" color="text.secondary" sx={sx.note}>
                    {item.note}
                  </Typography>
                ) : null}
                <Typography variant="caption" color="text.secondary">
                  {formatHistoryDateTime(item.createdAt)}
                </Typography>
              </Stack>
            </Box>
          ))}
        </Box>
      ) : null}
    </Paper>
  );
};

export default AccountStatusCard;

const DOT_COLORS: Record<AccountHistoryTone, string> = {
  ok: 'success.main',
  error: 'error.main',
  neutral: controlTokens.primary,
};

const sx = {
  paper: {
    borderRadius: radiusTokens.xxl,
    p: 2.5,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  list: { m: 0, p: 0, listStyle: 'none', display: 'flex', flexDirection: 'column' },
  item: { display: 'flex', gap: 1.5 },
  gutter: {
    width: 14,
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  dot: (tone: AccountHistoryTone) => ({
    width: 10,
    height: 10,
    mt: '5px',
    borderRadius: '50%',
    flexShrink: 0,
    bgcolor: DOT_COLORS[tone],
    opacity: tone === 'neutral' ? 0.35 : 1,
  }),
  line: { width: 2, flex: 1, minHeight: 14, my: 0.5, bgcolor: 'divider' },
  content: { pb: 1.75, minWidth: 0 },
  note: { fontStyle: 'italic', overflowWrap: 'anywhere' },
};
